// path: frontend/src/components/views/ProjectsView.jsx
/**
 * Вкладка «Проекты»: тулбар фильтров, канбан/список, футер.
 *
 * Тумблер «Все проекты» (filters.showAll, default=false):
 *   - ВЫКЛ — только свои (managerId === user.id ИЛИ есть задача, где
 *     user — assignee). Локальный фильтр в utils/filters.js::filterProjects.
 *   - ВКЛ — всё, что роль вправе видеть (бэк отдаёт scope роли,
 *     локальный фильтр «только мои» снимается).
 *   - Тумблер рендерится только при capabilities.seeAllContent
 *     (RBAC-решение на бэке: canSeeAllContent без project_lead —
 *     project_lead видит только свои, переключать нечего).
 *
 * `projects` из useTasksViewDb приходят уже scope-фильтрованными с
 * сервера (ProjectService._computeProjectVisibilityWithScope).
 * Параметр showAll в GET /projects — no-op для выбора scope (бэк всегда
 * отдаёт scope роли), но передаётся в query, чтобы queryKey React Query
 * включал showAll: смена тумблера триггерит re-fetch, а не переиспользует
 * кэш. Разница «всё vs свои» — локальная, в filterProjects.
 *
 * Для ролей без тумблера (project_lead, обычные исполнители) UX-фильтр
 * «только мои» не применяется (filtersForUtils.showAll=true): они и так
 * видят только свой scope (project_lead — свои проекты, executor —
 * проекты со своими задачами + accessUserIds), иначе потерялись бы
 * проекты с явным доступом без задач.
 *
 * Фактические часы (fact) берутся из project.actualHours (D-13).
 */
import { useState, useMemo, useRef, memo, useCallback } from "react";
import { useToast } from "../../context/ToastContext";
import { useConfirm } from "../../context/ConfirmContext";
import { useAuth } from "../../hooks/useAuth";
import Kanban from "../Kanban";
import { Select } from "../Select";
import { SortControl } from "../SortControl";
import { ToggleSwitch } from "../ToggleSwitch";
import { ViewFooter } from "../ViewFooter";
import { ViewModeToggle } from "../ViewModeToggle";
import { ListLayoutToggle } from "../ListLayoutToggle";
import { Ic } from "../Icons";
import { ICONS } from "../iconsData";
import Projects from "../Projects";
import { ProjectCard } from "../cards/ProjectCard";
import { EntitySelectDropdown } from "../EntitySelectDropdown";
import { useFilters } from "../../hooks/useFilters";
import { useSort, withSortDefaults } from "../../hooks/useSort";
import { useFilterOptions } from "../../hooks/useFilterOptions";
import { useTasksViewDb } from "../../hooks/useDb";
import { useProjectActions } from "../../hooks/useProjectActions";
import { useEventCallback } from "../../hooks/useEventCallback";
import { useDictionariesQuery } from "../../api/queries/dictionaries";
import {
  PROJECT_STATUSES,
  PROJECT_STATUS_CONFIG,
  PROJECT_STATUS_ORDER,
  PROJECT_TYPES,
  PROJECT_PRIORITIES,
} from "../../utils/constants";
import { projectSortValue, makeComparator } from "../../utils/sorting";
import { optionsFromMap } from "../../utils/selectOptions";
import { toEmployeeOption, toDeptOption } from "../../utils/selectOptions";
import {
  filterProjects,
  filterSelectClass,
  filterDateClass,
} from "../../utils/filters";

const INITIAL_FILTERS = withSortDefaults({
  projectId: "all",
  status: "all",
  type: "all",
  priority: "all",
  aircraftType: "all",
  participant: "all",
  managerId: "all",
  deptId: "all",
  tag: "all",
  endBefore: "",
  showAll: false,
});

const SORT_OPTIONS = [
  { value: "priority", label: "По приоритету" },
  { value: "start", label: "По дате начала" },
  { value: "end", label: "По дате окончания" },
  { value: "name", label: "По названию" },
  { value: "code", label: "По коду" },
  { value: "status", label: "По статусу" },
  { value: "planned", label: "По плану" },
  { value: "manager", label: "По ответственному" },
];

const NUMERIC_SORTS = new Set(["planned"]);
const STATUS_SELECT_OPTIONS = optionsFromMap(PROJECT_STATUSES, "Статус");
const TYPE_SELECT_OPTIONS = optionsFromMap(PROJECT_TYPES, "Тип");
const PRIORITY_SELECT_OPTIONS = optionsFromMap(PROJECT_PRIORITIES, "Приоритет");

const EMPTY_ARRAY = Object.freeze([]);

const isProjectVisibleInList = (p) => p.isActive;

const projectOptionValue = (p) => p.id;
const projectOptionLabel = (p) => (p.code ? `${p.code} - ${p.name}` : p.name);

function ProjectsView({
  ur,
  openProject,
  openCopyProject,
  openTemplateFromProject,
  openProjectAccess,
  openTag,
}) {
  const { capabilities } = useAuth();
  const { showToast } = useToast();
  const { confirm } = useConfirm();

  // Filters и showAll — до useTasksViewDb: showAll входит в queryKey
  // GET /projects, чтобы смена тумблера триггерила re-fetch.
  const { filters, setFilter } = useFilters(INITIAL_FILTERS);
  const { participant, managerId, deptId, showAll } = filters;

  const db = useTasksViewDb({ showAll });
  const { projects, tasks, employees, departments } = db;

  const [viewMode, setViewMode] = useState("kanban");
  const [listLayout, setListLayout] = useState("tiles");

  const [isEndFocused, setIsEndFocused] = useState(false);
  const endInputRef = useRef(null);

  const { data: dictionaries = {} } = useDictionariesQuery();

  const aircraftTypeSelectOptions = useMemo(() => {
    const set = new Set(dictionaries.aircraft_types || []);
    projects.forEach((p) => {
      if (p.aircraftType) set.add(p.aircraftType.trim());
    });
    const options = Array.from(set).sort((a, b) => a.localeCompare(b, "ru"));
    return [
      { value: "all", label: "Тип АТ" },
      ...options.map((t) => ({ value: t, label: t })),
    ];
  }, [dictionaries.aircraft_types, projects]);

  const canSeeAll = Boolean(capabilities.seeAllContent);
  const canCreate = Boolean(capabilities.createProject);

  const {
    field: sortField,
    dir: sortDir,
    handleFieldChange: handleSortFieldChange,
    handleDirToggle: handleSortDirToggle,
  } = useSort({ filters, setFilter, numericFields: NUMERIC_SORTS });

  const employeesById = useMemo(
    () => new Map(employees.map((e) => [e.id, e])),
    [employees],
  );

  const baseProjects = useMemo(
    () => projects.filter(isProjectVisibleInList),
    [projects],
  );

  const tagSelectOptions = useMemo(() => {
    const set = new Set();
    baseProjects.forEach((p) => {
      if (Array.isArray(p.tags)) p.tags.forEach((tag) => set.add(tag));
    });
    return [
      { value: "all", label: "Метка" },
      ...Array.from(set).map((tag) => ({ value: tag, label: tag })),
    ];
  }, [baseProjects]);

  const participantOptions = useMemo(() => {
    const activeProjectIds = new Set(
      projects.filter(isProjectVisibleInList).map((p) => p.id),
    );
    const involvedIds = new Set();
    tasks.forEach((t) => {
      if (activeProjectIds.has(t.projectId) && !t.archived && t.assigneeId) {
        involvedIds.add(t.assigneeId);
      }
    });
    return employees.filter((e) => involvedIds.has(e.id));
  }, [projects, tasks, employees]);

  const managerOptions = useMemo(() => {
    const ids = new Set();
    baseProjects.forEach((p) => {
      if (p.managerId) ids.add(p.managerId);
    });
    return employees.filter((e) => ids.has(e.id));
  }, [baseProjects, employees]);

  const deptOptions = useMemo(() => {
    const deptIds = new Set();
    participantOptions.forEach((e) =>
      e.departments.forEach((d) => deptIds.add(d.deptId)),
    );
    return departments.filter((d) => deptIds.has(d.id));
  }, [participantOptions, departments]);

  // Роли без тумблера (project_lead, executor) видят только свой scope —
  // UX-фильтр «только мои» им не нужен, иначе потерялись бы проекты с
  // явным accessUserIds без задач. Для canSeeAll-ролей фильтр управляется
  // тумблером через filters.showAll.
  const filtersForUtils = useMemo(
    () => (canSeeAll ? filters : { ...filters, showAll: true }),
    [filters, canSeeAll],
  );

  const tasksForFilter = useMemo(() => {
    const needMine = !filtersForUtils.showAll;
    const needParticipantOrDept = participant !== "all" || deptId !== "all";
    if (!needMine && !needParticipantOrDept) return null;
    return tasks;
  }, [tasks, filtersForUtils.showAll, participant, deptId]);

  const filteredProjects = useMemo(
    () =>
      filterProjects(baseProjects, filtersForUtils, {
        tasks: tasksForFilter ?? EMPTY_ARRAY,
        employeesById,
        currentUserId: ur.id,
      }),
    [baseProjects, filtersForUtils, tasksForFilter, employeesById, ur.id],
  );

  const sortedProjects = useMemo(() => {
    const valueOf = (p) => projectSortValue(p, sortField, { employeesById });
    return [...filteredProjects].sort(makeComparator(valueOf, sortDir));
  }, [filteredProjects, sortField, sortDir, employeesById]);

  const projectStatsById = useMemo(() => {
    const map = new Map();
    const byProject = new Map();
    tasks.forEach((t) => {
      if (t.archived) return;
      let list = byProject.get(t.projectId);
      if (!list) {
        list = [];
        byProject.set(t.projectId, list);
      }
      list.push(t);
    });
    byProject.forEach((list, projectId) => {
      const assigneeIds = [];
      const seen = new Set();
      list.forEach((t) => {
        if (t.assigneeId && !seen.has(t.assigneeId)) {
          seen.add(t.assigneeId);
          assigneeIds.push(t.assigneeId);
        }
      });
      map.set(projectId, { assigneeIds });
    });
    return map;
  }, [tasks]);

  const { handleMove: handleMoveProject } = useProjectActions({
    db,
    toast: showToast,
    confirm,
  });

  const renderProjectCard = useCallback(
    (project) => {
      const stats = projectStatsById.get(project.id) || { assigneeIds: [] };
      return (
        <ProjectCard
          project={project}
          assigneeIds={stats.assigneeIds}
          employeesById={employeesById}
          db={db}
          user={ur}
          openProject={openProject}
          openTag={openTag}
          onMove={handleMoveProject}
          onCopy={openCopyProject}
          onMakeTemplate={openTemplateFromProject}
          onOpenAccess={openProjectAccess}
        />
      );
    },
    [
      projectStatsById,
      employeesById,
      db,
      ur,
      openProject,
      openTag,
      handleMoveProject,
      openCopyProject,
      openTemplateFromProject,
      openProjectAccess,
    ],
  );

  const handleEndFocus = () => setIsEndFocused(true);
  const handleEndBlur = () => setIsEndFocused(false);

  const handleEndBeforeChange = (e) => {
    const value = e.target.value;
    setFilter("endBefore", value);
    if (!value) {
      setIsEndFocused(false);
      endInputRef.current?.blur();
    }
  };

  const handleShowAllChange = useEventCallback((v) => {
    setFilter("showAll", v);
  });

  const participantSelectOptions = useFilterOptions(
    participantOptions,
    "Участник",
    toEmployeeOption,
  );
  const managerSelectOptions = useFilterOptions(
    managerOptions,
    "Ответственный",
    toEmployeeOption,
  );
  const deptSelectOptions = useFilterOptions(
    deptOptions,
    "Отдел",
    toDeptOption,
  );

  return (
    <>
      <div className="toolbar toolbar-wrap">
        <EntitySelectDropdown
          items={baseProjects}
          value={filters.projectId}
          onChange={(v) => setFilter("projectId", v)}
          getOptionValue={projectOptionValue}
          getOptionLabel={projectOptionLabel}
          allLabel="Все проекты"
          placeholder="Проект"
          searchable
          className="filter-project-select"
        />
        <Select
          className={filterSelectClass(filters.status)}
          value={filters.status}
          onChange={(v) => setFilter("status", v)}
          options={STATUS_SELECT_OPTIONS}
        />
        <Select
          className={filterSelectClass(filters.type)}
          value={filters.type}
          onChange={(v) => setFilter("type", v)}
          options={TYPE_SELECT_OPTIONS}
        />
        <Select
          className={filterSelectClass(filters.priority)}
          value={filters.priority}
          onChange={(v) => setFilter("priority", v)}
          options={PRIORITY_SELECT_OPTIONS}
        />
        <Select
          className={filterSelectClass(filters.aircraftType)}
          value={filters.aircraftType}
          onChange={(v) => setFilter("aircraftType", v)}
          options={aircraftTypeSelectOptions}
        />
        <Select
          className={filterSelectClass(participant)}
          value={participant}
          onChange={(v) => setFilter("participant", v)}
          options={participantSelectOptions}
        />
        <Select
          className={filterSelectClass(managerId)}
          value={managerId}
          onChange={(v) => setFilter("managerId", v)}
          options={managerSelectOptions}
        />
        <Select
          className={filterSelectClass(filters.deptId, "filter-select-dept")}
          value={filters.deptId}
          onChange={(v) => setFilter("deptId", v)}
          options={deptSelectOptions}
        />
        <Select
          className={filterSelectClass(filters.tag)}
          value={filters.tag}
          onChange={(v) => setFilter("tag", v)}
          options={tagSelectOptions}
        />
        <span className={filterDateClass(filters.endBefore, isEndFocused)}>
          <input
            ref={endInputRef}
            type="date"
            className="inp filter-date-input"
            value={filters.endBefore}
            onChange={handleEndBeforeChange}
            onFocus={handleEndFocus}
            onBlur={handleEndBlur}
            aria-label="Окончание до"
          />
          {!filters.endBefore && (
            <span className="filter-date-hint" aria-hidden="true">
              Окончание до
            </span>
          )}
        </span>
        <SortControl
          options={SORT_OPTIONS}
          field={sortField}
          dir={sortDir}
          onFieldChange={handleSortFieldChange}
          onToggleDir={handleSortDirToggle}
        />
        {canCreate && (
          <button
            type="button"
            className="btn primary toolbar-action"
            onClick={() => openProject(null)}
          >
            <Ic d={ICONS.plus} size={15} /> Создать
          </button>
        )}
      </div>

      {viewMode === "kanban" ? (
        <Kanban
          items={sortedProjects}
          statusOrder={PROJECT_STATUS_ORDER}
          statusMap={PROJECT_STATUS_CONFIG}
          renderCard={renderProjectCard}
          onDrop={handleMoveProject}
          columns={4}
        />
      ) : (
        <Projects
          db={db}
          ur={ur}
          openProject={openProject}
          projects={sortedProjects}
          layout={listLayout}
          openTag={openTag}
          onMoveProject={handleMoveProject}
          openCopyProject={openCopyProject}
          openTemplateFromProject={openTemplateFromProject}
          openProjectAccess={openProjectAccess}
        />
      )}

      <ViewFooter>
        <ViewModeToggle value={viewMode} onChange={setViewMode} />
        {viewMode === "list" && (
          <ListLayoutToggle value={listLayout} onChange={setListLayout} />
        )}
        {canSeeAll && (
          <ToggleSwitch
            checked={showAll}
            onChange={handleShowAllChange}
            label="Все проекты"
          />
        )}
      </ViewFooter>
    </>
  );
}

export default memo(ProjectsView);
